import { useCallback, useRef, useState } from 'react';

import { hasRenderedContent, strokeHasRenderedContent } from './stroke-utils';

export type DoodleTool = 'brush' | 'eraser' | 'fill';

/**
 * SHARED CONTRACT (must match the backend exactly): a single doodle stroke.
 * `points` is an array of `[x, y, pressure?]` tuples in canvas coordinates
 * (800x500). `fill` strokes carry a single seed point + color and a size of 0.
 */
export interface DoodleStroke {
	points: number[][];
	color: string;
	size: number;
	tool: DoodleTool;
}

export const DOODLE_COLORS = [
	{ hex: '#1a1a1a', name: 'black' },
	{ hex: '#e03131', name: 'red' },
	{ hex: '#2f9e44', name: 'green' },
	{ hex: '#1971c2', name: 'blue' },
	{ hex: '#f08c00', name: 'orange' },
	{ hex: '#9c36b5', name: 'purple' },
	{ hex: '#0c8599', name: 'teal' },
	{ hex: '#e8590c', name: 'coral' },
] as const;

export const DOODLE_SIZES = [
	{ value: 1, dot: 3, name: 'extra_small' },
	{ value: 2, dot: 5, name: 'small' },
	{ value: 4, dot: 8, name: 'medium' },
	{ value: 8, dot: 12, name: 'large' },
	{ value: 14, dot: 16, name: 'extra_large' },
] as const;

interface StrokeHistory {
	strokes: DoodleStroke[];
	redoStack: DoodleStroke[];
}

/**
 * Stroke-history + tool state for the doodle editor. Pass `initialStrokes`
 * to seed the canvas from a backend-generated doodle (the "Edit doodle" flow)
 * — the redo stack starts empty so the seeded strokes can be undone but not
 * "redone" past their starting state.
 */
export const useDoodle = (initialStrokes?: DoodleStroke[]) => {
	const [tool, setTool] = useState<DoodleTool>('brush');
	const [color, setColor] = useState<string>(DOODLE_COLORS[0].hex);
	const [size, setSize] = useState<number>(DOODLE_SIZES[1].value);
	const [history, setHistory] = useState<StrokeHistory>(() => ({ strokes: initialStrokes ? [...initialStrokes] : [], redoStack: [] }));
	const [currentStroke, setCurrentStroke] = useState<DoodleStroke | null>(null);
	const currentStrokeRef = useRef<DoodleStroke | null>(null);

	const startStroke = useCallback(
		(point: number[]) => {
			setHistory((h) => ({ strokes: h.strokes, redoStack: [] }));
			const stroke: DoodleStroke = { points: [point], color, size, tool };
			currentStrokeRef.current = stroke;
			setCurrentStroke(stroke);
		},
		[color, size, tool]
	);

	const addPoint = useCallback((point: number[]) => {
		const prev = currentStrokeRef.current;
		if (!prev) return;
		const updated = { ...prev, points: [...prev.points, point] };
		currentStrokeRef.current = updated;
		setCurrentStroke(updated);
	}, []);

	const endStroke = useCallback(() => {
		const completed = currentStrokeRef.current;
		currentStrokeRef.current = null;
		setCurrentStroke(null);
		if (completed) {
			setHistory((h) => ({ strokes: [...h.strokes, completed], redoStack: [] }));
		}
	}, []);

	const performFill = useCallback(
		(point: number[]) => {
			const fillStroke: DoodleStroke = { points: [point], color, size: 0, tool: 'fill' };
			setHistory((h) => ({ strokes: [...h.strokes, fillStroke], redoStack: [] }));
		},
		[color]
	);

	const undo = useCallback(() => {
		setHistory((h) => {
			if (h.strokes.length === 0) return h;
			const removed = h.strokes[h.strokes.length - 1];
			return { strokes: h.strokes.slice(0, -1), redoStack: [...h.redoStack, removed] };
		});
	}, []);

	const redo = useCallback(() => {
		setHistory((h) => {
			if (h.redoStack.length === 0) return h;
			const restored = h.redoStack[h.redoStack.length - 1];
			return { strokes: [...h.strokes, restored], redoStack: h.redoStack.slice(0, -1) };
		});
	}, []);

	const clear = useCallback(() => {
		currentStrokeRef.current = null;
		setHistory({ strokes: [], redoStack: [] });
		setCurrentStroke(null);
	}, []);

	const { strokes, redoStack } = history;
	const canUndo = strokes.length > 0;
	const canRedo = redoStack.length > 0;
	// Gate Save on RENDERED content, not array length: a doodle made only of
	// degenerate taps/flicks (outline of <4 points) rasterizes to a blank white
	// PNG. A stroke has to actually paint pixels to enable Save.
	const hasContent = hasRenderedContent(strokes) || (currentStroke !== null && strokeHasRenderedContent(currentStroke));

	return {
		tool,
		setTool,
		color,
		setColor,
		size,
		setSize,
		strokes,
		currentStroke,
		startStroke,
		addPoint,
		endStroke,
		performFill,
		undo,
		redo,
		clear,
		canUndo,
		canRedo,
		hasContent,
	};
};
